import { useEffect, useRef } from 'react';
import { parseDateOnly } from '../../utils/formatters';
import type { Version } from '../../services/versionService';
import { VersionTypeBadge } from './VersionTypeBadge';
import { VersionAreaBadge } from './VersionAreaBadge';
import { RupturaBadge } from './RupturaBadge';

interface ReleaseGroupProps {
  readonly versionCode: string;
  readonly versions: Version[];
  readonly highlighted?: boolean;
}

export function ReleaseGroup({ versionCode, versions, highlighted }: ReleaseGroupProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlighted && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [highlighted]);

  const latestDate = versions[0]?.fecha;
  const dateLabel = latestDate
    ? parseDateOnly(latestDate).toLocaleDateString('es-CL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

  const hasRuptura = versions.some(v => v.es_ruptura);

  return (
    <div
      ref={ref}
      className={`border-b border-gray-100 py-4 ${highlighted ? '' : 'first:pt-0'} last:border-0 last:pb-0 rounded-lg transition-colors ${
        highlighted ? 'bg-blue-50 px-4 pt-5 -mx-4 border-blue-200' : ''
      }`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`text-sm font-bold ${highlighted ? 'text-blue-700' : 'text-gray-900'}`}>
          v{versionCode}
        </span>
        {highlighted && (
          <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Nuevo</span>
        )}
        {hasRuptura && <RupturaBadge />}
        {dateLabel && (
          <span className="text-xs text-gray-400">{dateLabel}</span>
        )}
      </div>
      <ul className="space-y-2">
        {versions.map(version => (
          <li key={version.id} className="flex items-start gap-2 flex-wrap">
            <VersionTypeBadge tipo={version.tipo} />
            <VersionAreaBadge area={version.area} />
            <span className="text-sm text-gray-700 leading-5 mt-0.5">{version.descripcion}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
